import { groupBy, StorageAccess, valueOfEnum } from '@ianesthesie-kit/core';
import {
  ID,
  ListApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import {
  MedicalDocument,
  MedicalDocumentType,
  prescriptionDocumentTypes
} from './medicalDocument.model';
import { ListMedicalService, MedicalResponse } from '../baseMedical.service';
import { GroupFunction } from '@/util';
import axios, { AxiosResponse } from 'axios';

export class MedicalDocumentService extends ListMedicalService<MedicalDocument> {
  private static INSTANCE_BY_ID: MedicalDocumentService;
  private static readonly DELETE_WARNING_MUTED_KEY = 'deleteMediumWarningMuted';

  private constructor(patientId: string) {
    super('/medical-document', patientId);
  }

  public static get isDeleteWarningMuted() {
    return (
      StorageAccess.getInstance().getItem(this.DELETE_WARNING_MUTED_KEY) ===
      'true'
    );
  }

  public static muteDeleteWarning() {
    StorageAccess.getInstance().setItem(this.DELETE_WARNING_MUTED_KEY, 'true');
  }

  public static getInstance(patientId: ID): MedicalDocumentService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new MedicalDocumentService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  public static isAiVisionEligible(arg: MedicalDocumentType | MedicalDocument) {
    const t = typeof arg === 'object' ? arg.type : arg;
    return (
      t === MedicalDocumentType.PRESCRIPTION ||
      t === MedicalDocumentType.BLOODWORK ||
      t === MedicalDocumentType.CARDIAC_TEST ||
      t === MedicalDocumentType.OPERATOR_INVOICE
    );
  }

  public static isDeletable(arg: MedicalDocumentType | MedicalDocument) {
    const t = typeof arg === 'object' ? arg.type : arg;
    return (
      !!t &&
      t !== MedicalDocumentType.COMPLETE_REPORT &&
      !prescriptionDocumentTypes.includes(t)
    );
  }

  upsert(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    item: Partial<MedicalDocument>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params: Record<string, any>
  ): Promise<MedicalResponse<MedicalDocument, true>> {
    throw new Error('Unsupported operation');
  }

  upload(
    document: Partial<MedicalDocument>,
    files?: File[],
    onUploadProgress?: (e: ProgressEvent) => void
  ): Promise<MedicalResponse<MedicalDocument, true>> {
    const formData = new FormData();
    const doc = {
      id: document.id,
      type: document.type,
      date: document.date,
      patientVisible: document.patientVisible,
      mediaIds: document.media?.map((d) => d.id)
    };
    formData.append('document', JSON.stringify(doc));
    files?.forEach((f) => {
      formData.append('files', f);
    });

    return axios
      .post<ListApiResponse<MedicalDocument>>(this.url, formData, {
        onUploadProgress
      })
      .then((response: AxiosResponse<ListApiResponse<MedicalDocument>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .then(this.stallResponse)
      .catch((err) => {
        // todo handle error
        // todo sentry
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  deleteMedium(
    document: MedicalDocument,
    mediumId: ID
  ): Promise<MedicalResponse<MedicalDocument, true>> {
    if (!mediumId || !document.media?.some((m) => m.id === mediumId)) {
      throw new Error('Medium not found in document');
    }
    return axios
      .delete(`${this.url}/${document.id}/medium/${mediumId}`)
      .then((response: AxiosResponse<ListApiResponse<MedicalDocument>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .then(this.stallResponse)
      .catch((err) => {
        // todo handle error
        // todo sentry
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  triage(doc: MedicalDocument): Promise<void> {
    if (!MedicalDocumentService.isAiVisionEligible(doc)) {
      throw new Error('Triage not supported for this document');
    }

    if (doc.extractionStatus === 'PENDING') {
      throw new Error('An extraction is already in progress');
    }

    return axios
      .post<SuccessApiResponse>(`${this.url}/${doc.id}/triage`)
      .then(async () => {
        await this.stallResponse(undefined);
      })
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected getGroupingFunction<K extends string, L extends boolean = true>(): {
    apply: GroupFunction<MedicalDocument, K, L>;
  } {
    return {
      apply: (data) => groupBy(data, 'type')
    };
  }

  protected transformResponseEntity(data: any): MedicalDocument {
    return {
      ...super.transformResponseEntity(data),
      type: valueOfEnum(data.type, MedicalDocumentType, {
        throwIfNotFound: true,
        caseSensitive: false
      })!
    };
  }

  private async stallResponse<T extends any | undefined>(resp: T) {
    // wait a bit before we resolve the promise (and thus invalidating docs), so extraction status is set on backend
    await new Promise((r) => setTimeout(r, 250));
    return resp;
  }
}
